import { lazy, Suspense } from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { Loading, Toaster } from '@loyalty-system/react-web-ui-shadcn';

import AllTheProviders from './components/all-the-providers';
import ChartDefaultConfigs from './components/charts/chart-default-configs';
import PrivateRoute from './modules/auth/components/private-route';
import PublicRoute from './modules/auth/components/public-route';

const PageRedirect = lazy(() => import('./pages/redirect'));

const PageNotFound = lazy(() => import('./pages/not-found'));

const PageLogin = lazy(() => import('./pages/login'));

const PageDashboard = lazy(() => import('./pages/dashboard'));

const PageCategoryList = lazy(() => import('./pages/categories'));
const PageCategoryEdit = lazy(() => import('./pages/categories/edit'));
const PageCategoryNew = lazy(() => import('./pages/categories/new'));

const PagePostList = lazy(() => import('./pages/posts'));
const PagePostEdit = lazy(() => import('./pages/posts/edit'));
const PagePostNew = lazy(() => import('./pages/posts/new'));

const PageUserList = lazy(() => import('./pages/users'));
const PageUserEdit = lazy(() => import('./pages/users/edit'));
const PageUserNew = lazy(() => import('./pages/users/new'));

const PageFileList = lazy(() => import('./pages/files'));

const PageContacts = lazy(() => import('./pages/contacts'));

const PageSettings = lazy(() => import('./pages/settings'));

const PageProfile = lazy(() => import('./pages/profiles'));

const PageNotifications = lazy(() => import('./pages/notifications'));

const PageAuditLogs = lazy(() => import('./pages/audit-logs'));

const PageMultiStepForm = lazy(() => import('./pages/multi-step-form'));

const PageRuleList = lazy(() => import('./pages/rules'));
const PageRuleEdit = lazy(() => import('./pages/rules/edit'));
const PageRuleNew = lazy(() => import('./pages/rules/new'));

const PageDocumentation = lazy(() => import('./pages/documentation'));

const PageEvidenceList = lazy(() => import('./pages/evidences'));

const PageRedemptionList = lazy(() => import('./pages/redemption'));

const PageRewardCategoryList = lazy(() => import('./pages/reward-categories'));
const PageRewardCategoryNew = lazy(() => import('./pages/reward-categories/new'));
const PageRewardCategoryEdit = lazy(() => import('./pages/reward-categories/edit'));

const PageRewardList = lazy(() => import('./pages/rewards'));
const PageRewardNew = lazy(() => import('./pages/rewards/new'));
const PageRewardEdit = lazy(() => import('./pages/rewards/edit'));

const PageBannerList = lazy(() => import('./pages/banners'));
const PageBannerNew = lazy(() => import('./pages/banners/new'));
const PageBannerEdit = lazy(() => import('./pages/banners/edit'));

const PageUserServiceList = lazy(() => import('./pages/user-services'));

const PageDefaultServiceList = lazy(() => import('./pages/default-services'));
const PageDefaultServiceNew = lazy(() => import('./pages/default-services/new'));
const PageDefaultServiceEdit = lazy(() => import('./pages/default-services/edit'));

const PageDynamicPoint = lazy(() => import('./pages/dynamic-point'));

const App = () => {
  return (
    <Router>
      <AllTheProviders>
        <Suspense
          fallback={
            <div className="flex h-screen items-center justify-center">
              <Loading />
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<PageRedirect />} />
            <Route path="/:locale" element={<PageRedirect />} />
            <Route element={<PublicRoute />}>
              <Route path="/:locale/login" element={<PageLogin />} />
            </Route>
            <Route element={<PrivateRoute />}>
              <Route path="/:locale/rule-configuration" element={<PageRuleList />} />
              <Route path="/:locale/rule-configuration/new" element={<PageRuleNew />} />
              <Route path="/:locale/rule-configuration/:id/edit" element={<PageRuleEdit />} />
              <Route path="/:locale/evidences" element={<PageEvidenceList />} />
              <Route path="/:locale/reward-categories" element={<PageRewardCategoryList />} />
              <Route path="/:locale/reward-categories/new" element={<PageRewardCategoryNew />} />
              <Route path="/:locale/reward-categories/:id/edit" element={<PageRewardCategoryEdit />} />
              <Route path="/:locale/rewards" element={<PageRewardList />} />
              <Route path="/:locale/rewards/new" element={<PageRewardNew />} />
              <Route path="/:locale/rewards/:id/edit" element={<PageRewardEdit />} />
              <Route path="/:locale/banners" element={<PageBannerList />} />
              <Route path="/:locale/banners/new" element={<PageBannerNew />} />
              <Route path="/:locale/banners/:id/edit" element={<PageBannerEdit />} />
              <Route path="/:locale/user-services" element={<PageUserServiceList />} />
              <Route path="/:locale/default-services" element={<PageDefaultServiceList />} />
              <Route path="/:locale/default-services/new" element={<PageDefaultServiceNew />} />
              <Route path="/:locale/default-services/:id/edit" element={<PageDefaultServiceEdit />} />
              <Route path="/:locale/dashboard" element={<PageDashboard />} />
              <Route path="/:locale/files" element={<PageFileList />} />
              <Route path="/:locale/users" element={<PageUserList />} />
              <Route path="/:locale/users/new" element={<PageUserNew />} />
              <Route path="/:locale/users/:id/edit" element={<PageUserEdit />} />
              <Route path="/:locale/profile/:type" element={<PageProfile />} />
              <Route path="/:locale/categories" element={<PageCategoryList />} />
              <Route path="/:locale/categories/new" element={<PageCategoryNew />} />
              <Route path="/:locale/categories/:id/edit" element={<PageCategoryEdit />} />
              <Route path="/:locale/posts" element={<PagePostList />} />
              <Route path="/:locale/posts/new" element={<PagePostNew />} />
              <Route path="/:locale/posts/:id/edit" element={<PagePostEdit />} />
              <Route path="/:locale/audit-logs" element={<PageAuditLogs />} />
              <Route path="/:locale/contacts" element={<PageContacts />} />
              <Route path="/:locale/settings/:type" element={<PageSettings />} />
              <Route path="/:locale/notifications/:type" element={<PageNotifications />} />
              <Route path="/:locale/documentation/:type" element={<PageDocumentation />} />
              <Route path="/:locale/multi-step-form" element={<PageMultiStepForm />} />
              <Route path="/:locale/redemption" element={<PageRedemptionList />} />
              <Route path="/:locale/dynamic-point" element={<PageDynamicPoint />} />
            </Route>
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </Suspense>
        <Toaster />
        <ChartDefaultConfigs />
        <div className="transform-gpu"></div>
      </AllTheProviders>
    </Router>
  );
};

export default App;
